/**
 * DataAnchoredInvitations — 3 data-anchored invitation cards above the chat
 * zone on /home. Fork V2.6 Worker-C (2026-04-20).
 *
 * Each card carries a synthesized question like *"Why did Sunday Apr 19 peak
 * at $208K vs Saturday?"* generated by the backend `synthesizeQuestions`
 * engine against the active event's real numbers. Clicking a card seeds the
 * chat with the question text so Brian's first paint on /home answers "what
 * about MY event right now" instead of presenting a generic empty prompt.
 *
 * Contract (matches docs/v26/WORKER-AC-INTERFACE.md):
 *   POST /api/suggestions/synthesized { event_id, max }
 *   → { version, scope, synthesized, count, questions: [{ id, text, category,
 *       anchor?: { label, value } | null }] }
 *
 * Lifecycle:
 *   - Mounts on /home when `useAuth().currentEventId` is non-null.
 *   - POSTs once per `currentEventId` change (no polling — re-renders if the
 *     sidebar event switches).
 *   - 404 = backend flag OFF → graceful empty state (single neutral card).
 *   - 500 = synthesis failure → same graceful empty card; logs a warn.
 *   - Non-`ok` non-404 / network error → same empty card.
 *
 * Accessibility:
 *   - Each card is a native `<button>` (keyboard focus, enter/space activates).
 *   - Anchor chip carries `aria-label` matching its label + value so the
 *     screen reader reads "Sunday gross $208,000" when tabbing onto the card.
 *
 * Styling: Butler midway-blue + magenta tokens from tailwind.config.js;
 * MagicMoney admin neutral DNA (hairline borders, card-soft shadow). No
 * colors inline — all via Tailwind utilities so dark-mode inversion works
 * without a second pass.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowRight, Sparkles } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useChat } from '@/contexts/ChatContext'

export interface InvitationQuestion {
  id: string
  text: string
  category?: string
  anchor?: { label: string; value: string } | null
}

interface SynthesizedResponse {
  version?: number
  scope?: string
  synthesized?: boolean
  count?: number
  questions?: InvitationQuestion[]
}

type LoadState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ok'; questions: InvitationQuestion[] }
  | { status: 'empty' }
  | { status: 'error' }

const CARD_COUNT = 3

function isInvitationQuestion(value: unknown): value is InvitationQuestion {
  if (!value || typeof value !== 'object') return false
  const v = value as Record<string, unknown>
  if (typeof v.id !== 'string' || v.id.length === 0) return false
  if (typeof v.text !== 'string' || v.text.length === 0) return false
  if (
    v.anchor !== undefined &&
    v.anchor !== null &&
    (typeof v.anchor !== 'object' ||
      typeof (v.anchor as { label?: unknown }).label !== 'string' ||
      typeof (v.anchor as { value?: unknown }).value !== 'string')
  ) {
    return false
  }
  return true
}

interface DataAnchoredInvitationsProps {
  /**
   * Test seam — lets unit tests swap in a deterministic fetcher without
   * having to stub global fetch. Production callers leave this unset so the
   * component hits the real backend.
   */
  fetcher?: typeof fetch
  'data-testid'?: string
}

export function DataAnchoredInvitations({
  fetcher,
  'data-testid': testId = 'data-anchored-invitations',
}: DataAnchoredInvitationsProps = {}) {
  const { currentEventId } = useAuth()
  // Codex V2.6 Worker-C review 2026-04-20 HIGH #1 mitigation: the
  // streaming pipe in ChatContext tracks only ONE active turn via
  // `streamingMessageIdRef`. If a user clicks an invitation while a
  // prior stream is still in flight, the second turn would reset the
  // ref and later chunks from turn 1 could attach to the wrong bubble.
  // The architectural fix is request-id correlation at the WS layer
  // (cross-cutting; out of scope for this fork), so we guard the cards
  // here: disable them while isTyping || isStreaming. A click during a
  // stream becomes a no-op instead of starting an overlapping turn.
  const { sendMessage, isTyping, isStreaming } = useChat() as {
    sendMessage: (text: string) => void
    isTyping?: boolean
    isStreaming?: boolean
  }
  const chatBusy = Boolean(isTyping || isStreaming)
  const [state, setState] = useState<LoadState>({ status: 'idle' })

  // Per-request abort so sidebar-event switches don't land stale results on
  // the newer request. Keeps the component stable against the V2.5-A/B
  // race pattern (see reference_live_feed_ui_patterns_20260419.md §4).
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    if (currentEventId == null) {
      setState({ status: 'idle' })
      return
    }
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    const doFetch: typeof fetch = fetcher
      ? fetcher
      : (input, init) => fetch(input, init)

    setState({ status: 'loading' })

    doFetch('/api/suggestions/synthesized', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ event_id: currentEventId, max: CARD_COUNT }),
      signal: controller.signal,
    })
      .then(async (res) => {
        if (controller.signal.aborted) return
        if (res.status === 404) {
          setState({ status: 'empty' })
          return
        }
        if (!res.ok) {
          // eslint-disable-next-line no-console
          console.warn(
            '[data-anchored-invitations] non-ok response:',
            res.status,
          )
          setState({ status: 'error' })
          return
        }
        const data = (await res.json()) as SynthesizedResponse
        // Codex V2.6 Worker-C pass-2 HIGH #1 (2026-04-20): Worker A's
        // engine returns `synthesized: false` on the fallback-gallery
        // path (eventId null / butler unreachable / <3 synthesized
        // items). That signal MUST gate the "data-anchored" render;
        // otherwise the card strip would show generic gallery prompts
        // under the "Data-anchored starters for your active event"
        // heading — actively misleading. Treat `synthesized === false`
        // as equivalent to the empty state so the fallback card surfaces
        // instead.
        if (data?.synthesized === false) {
          setState({ status: 'empty' })
          return
        }
        const questions = Array.isArray(data?.questions)
          ? data.questions.filter(isInvitationQuestion).slice(0, CARD_COUNT)
          : []
        if (questions.length === 0) {
          setState({ status: 'empty' })
          return
        }
        setState({ status: 'ok', questions })
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        if ((err as { name?: string })?.name === 'AbortError') return
        // eslint-disable-next-line no-console
        console.warn('[data-anchored-invitations] fetch failed:', err)
        setState({ status: 'error' })
      })

    return () => {
      controller.abort()
    }
  }, [currentEventId, fetcher])

  const handleAsk = useCallback(
    (q: InvitationQuestion) => {
      if (!q.text) return
      if (chatBusy) return
      sendMessage(q.text)
    },
    [chatBusy, sendMessage],
  )

  const cards = useMemo<InvitationQuestion[]>(() => {
    if (state.status === 'ok') return state.questions
    return []
  }, [state])

  // Hide the strip entirely when there's no event selected; /home already
  // renders a "pick one from the sidebar" hint via CurrentlyOpenEvents.
  if (currentEventId == null) return null

  return (
    <section
      data-testid={testId}
      aria-label="Ready-to-ask invitations"
      className="border-b border-hairline bg-white/60 backdrop-blur-sm dark:border-slate-700 dark:bg-slate-900/50"
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-4 pt-3">
        <Sparkles
          className="h-3.5 w-3.5 shrink-0 text-marquee-500"
          aria-hidden="true"
        />
        <span className="shrink-0 font-display text-[11px] font-semibold uppercase tracking-[0.18em] text-butler-700 dark:text-butler-200">
          Ready to dig in
        </span>
        <span className="min-w-0 text-[11px] text-ink-soft dark:text-slate-400">
          Data-anchored starters for your active event
        </span>
      </div>

      {state.status === 'loading' ? (
        <div
          data-testid={`${testId}-loading`}
          className="grid grid-cols-1 gap-3 px-4 py-3 md:grid-cols-3"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          {Array.from({ length: CARD_COUNT }, (_, i) => (
            <div
              key={i}
              className="h-24 animate-pulse rounded-card border border-hairline bg-surface-stripe dark:border-slate-700 dark:bg-slate-800"
            />
          ))}
          <span className="sr-only">Loading invitations…</span>
        </div>
      ) : state.status === 'ok' ? (
        <ul
          role="list"
          data-testid={`${testId}-list`}
          className="grid grid-cols-1 gap-3 px-4 py-3 md:grid-cols-3"
        >
          {cards.map((q) => (
            <li key={q.id} className="h-full">
              <button
                type="button"
                data-testid={`invitation-card-${q.id}`}
                onClick={() => handleAsk(q)}
                disabled={chatBusy}
                aria-disabled={chatBusy || undefined}
                className="group relative flex h-full w-full flex-col items-start gap-2 rounded-card border border-hairline bg-white px-4 py-3 text-left shadow-card-soft transition hover:-translate-y-0.5 hover:border-butler-300 hover:shadow-card focus:outline-none focus:ring-2 focus:ring-butler-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:-translate-y-0 disabled:hover:border-hairline disabled:hover:shadow-card-soft dark:border-slate-700 dark:bg-slate-800 dark:hover:border-butler-500 dark:disabled:hover:border-slate-700"
              >
                <span
                  className="line-clamp-3 font-display text-sm font-semibold leading-snug text-butler-800 dark:text-butler-100"
                  title={q.text}
                >
                  {q.text}
                </span>
                <div className="mt-auto flex w-full items-center justify-between gap-2">
                  {q.anchor ? (
                    <span
                      data-testid={`invitation-anchor-${q.id}`}
                      aria-label={`${q.anchor.label} ${q.anchor.value}`}
                      className="inline-flex items-center gap-1 rounded-full bg-butler-50 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-butler-700 ring-1 ring-butler-100 dark:bg-butler-900/40 dark:text-butler-100 dark:ring-butler-800"
                    >
                      {q.anchor.value}
                    </span>
                  ) : (
                    <span className="text-[11px] text-ink-soft dark:text-slate-400">
                      {q.category ? q.category : 'Data-anchored'}
                    </span>
                  )}
                  <span
                    aria-hidden="true"
                    className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-butler-600 transition group-hover:text-marquee-600 dark:text-butler-200 dark:group-hover:text-marquee-400"
                  >
                    Ask
                    <ArrowRight className="h-3 w-3" />
                  </span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      ) : state.status === 'empty' || state.status === 'error' ? (
        <div
          data-testid={`${testId}-empty`}
          className="grid grid-cols-1 gap-3 px-4 py-3"
        >
          <button
            type="button"
            data-testid="invitation-card-fallback"
            disabled={chatBusy}
            aria-disabled={chatBusy || undefined}
            onClick={() => {
              if (chatBusy) return
              sendMessage('Show me the top headlines for my active event')
            }}
            className="group flex items-center justify-between gap-2 rounded-card border border-dashed border-hairline bg-white/80 px-4 py-3 text-left shadow-card-soft transition hover:-translate-y-0.5 hover:border-butler-300 hover:shadow-card focus:outline-none focus:ring-2 focus:ring-butler-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:-translate-y-0 disabled:hover:border-hairline disabled:hover:shadow-card-soft dark:border-slate-700 dark:bg-slate-800/80 dark:hover:border-butler-500 dark:disabled:hover:border-slate-700"
          >
            <span className="font-display text-sm font-semibold text-butler-800 dark:text-butler-100">
              Show me the top headlines for my active event
            </span>
            <span
              aria-hidden="true"
              className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-butler-600 group-hover:text-marquee-600 dark:text-butler-200 dark:group-hover:text-marquee-400"
            >
              Ask
              <ArrowRight className="h-3 w-3" />
            </span>
          </button>
        </div>
      ) : null}
    </section>
  )
}
